import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PlanProvider } from './contexts/PlanContext';
import { AccountProvider } from './contexts/AccountContext';
import { CategoryProvider } from './contexts/CategoryContext';
import { TransactionProvider } from './contexts/TransactionContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import ProtectedRoute from './components/ProtectedRoute';
import OnboardingGuard from './components/OnboardingGuard';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import { PWAInstallButton } from './components/PWAInstallButton';
import { IOSInstallPrompt } from './components/IOSInstallPrompt';
import { UpdateNotification } from './components/UpdateNotification';
import HomePage from './pages/HomePage';
import BudgetPage from './pages/BudgetPage';
import { RekeningPage } from './pages/RekeningPage';
import MultiTransactionPage from './pages/MultiTransactionPage';
import HistoryPage from './pages/HistoryPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import DisplaySettingsPage from './pages/DisplaySettingsPage';
import ConnectChatGPTPage from './pages/ConnectChatGPTPage';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import WaitlistConfirmation from './pages/WaitlistConfirmation';
import OAuthConsentPage from './pages/OAuthConsentPage';

// Comprehensive onboarding flow
import OnboardingStep1 from './pages/onboarding/OnboardingStep1';
import OnboardingStep2 from './pages/onboarding/OnboardingStep2';
import OnboardingStep3 from './pages/onboarding/OnboardingStep3';
import OnboardingStep4 from './pages/onboarding/OnboardingStep4';
import OnboardingStep5 from './pages/onboarding/OnboardingStep5';
import OnboardingStep6 from './pages/onboarding/OnboardingStep6';
import OnboardingStep7 from './pages/onboarding/OnboardingStep7';
import OnboardingStep8 from './pages/onboarding/OnboardingStep8';
import OnboardingStep9 from './pages/onboarding/OnboardingStep9';
import OnboardingStep10 from './pages/onboarding/OnboardingStep10';
import OnboardingStep11 from './pages/onboarding/OnboardingStep11';
import OnboardingStep12 from './pages/onboarding/OnboardingStep12';
import OnboardingStep13 from './pages/onboarding/OnboardingStep13';
import OnboardingStep14 from './pages/onboarding/OnboardingStep14';

import { LayoutProvider, useLayout } from './contexts/LayoutContext';
import SidePanel from './components/SidePanel';

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSidebarOpen } = useLayout();
  const currentPath = location.pathname;

  const getPageTitle = () => {
    switch (currentPath) {
      case '/':
        return 'Kamu';
      case '/budget':
        return 'Anggaran';
      case '/rekening':
        return 'Rekening';
      case '/transactions':
        return 'Transaksi';
      case '/history':
        return 'Riwayat';
      case '/profile':
        return 'Profil';
      default:
        return 'Fittory';
    }
  };

  const isAuthPage = ['/login', '/register', '/auth/callback', '/waitlist-confirmation', '/oauth/consent'].includes(currentPath);
  const isOnboardingPage = currentPath.startsWith('/onboarding');
  const isAddPage = currentPath === '/add';
  const isEditTransactionPage = currentPath.match(/^\/transactions\/[^/]+\/edit$/) || currentPath === '/transactions/bulk-edit'; // Standalone edit/bulk-edit transaction page
  const isHomePage = currentPath === '/'; // HomePage has its own header
  const isBudgetPage = currentPath === '/budget'; // Budget page has its own header
  const isHistoryPage = currentPath === '/history'; // History page has its own header
  const isRekeningPage = currentPath === '/rekening'; // Rekening page has its own header
  const isProfilePage = currentPath === '/profile'; // Profile page has its own header
  const isEditProfilePage = currentPath === '/profile/edit'; // Edit profile page has its own header
  const isChangePasswordPage = currentPath === '/profile/change-password'; // Change password page has its own header
  const isDisplaySettingsPage = currentPath === '/profile/display'; // Currency settings page has its own header
  const isConnectChatGPTPage = currentPath === '/profile/connect-chatgpt'; // Connect ChatGPT page has its own header

  // Pages that should NOT have the main layout (sidebar/bottom nav)
  const isStandalonePage = isAuthPage || isOnboardingPage;

  // Pages that should NOT have the bottom nav (but might have sidebar in future designs? keeping consistent for now)
  // Actually, Add Page takes over the screen on mobile usually.
  const hideNav = isStandalonePage || isAddPage || isEditTransactionPage;

  // Determine navigation handler
  const handleNavigate = (page: string) => {
    const route = page === 'home' ? '/' : `/${page}`;
    navigate(route);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Side Panel - Desktop Only */}
      {!hideNav && (
        <SidePanel
          currentPage={currentPath === '/' ? 'home' : currentPath.substring(1)}
          onNavigate={handleNavigate}
        />
      )}

      {/* Main Content Wrapper - Adjusts with Sidebar */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${!hideNav ? (isSidebarOpen ? 'lg:ml-64' : 'lg:ml-0') : ''
        }`}>
        {!isStandalonePage && !isAddPage && !isEditTransactionPage && !isHomePage && !isBudgetPage && !isHistoryPage && !isRekeningPage && !isProfilePage && !isEditProfilePage && !isChangePasswordPage && !isDisplaySettingsPage && !isConnectChatGPTPage && (
          <Header title={getPageTitle()} />
        )}
        <main className="flex-1">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/waitlist-confirmation" element={<WaitlistConfirmation />} />
            <Route path="/oauth/consent" element={<OAuthConsentPage />} />

            {/* Comprehensive onboarding flow routes */}
            <Route
              path="/onboarding/step-1"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep1 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-2"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep2 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-3"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep3 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-4"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep4 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-5"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep5 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-6"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep6 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-7"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep7 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-8"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep8 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-9"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep9 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-10"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep10 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-11"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep11 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-12"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep12 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-13"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep13 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/step-14"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <OnboardingStep14 />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <HomePage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/budget"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <BudgetPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/rekening"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <RekeningPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/add"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <MultiTransactionPage onBack={() => window.history.back()} />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />

            {/* Standalone edit transaction page */}
            <Route
              path="/transactions/:id/edit"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <MultiTransactionPage mode="edit" onBack={() => window.history.back()} />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />

            {/* Bulk edit transactions page */}
            <Route
              path="/transactions/bulk-edit"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <MultiTransactionPage mode="edit" onBack={() => window.history.back()} />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <HistoryPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <ProfilePage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/edit"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <EditProfilePage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/change-password"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <ChangePasswordPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/display"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <DisplaySettingsPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/connect-chatgpt"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <ConnectChatGPTPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* Bottom Nav - Mobile Only */}
        {!hideNav && (
          <div className="lg:hidden">
            <BottomNav
              currentPage={currentPath === '/' ? 'home' : currentPath.substring(1)}
              onNavigate={handleNavigate}
            />
          </div>
        )}

        {/* PWA Install Button (Android/Desktop) */}
        <PWAInstallButton />

        {/* iOS Install Instructions */}
        <IOSInstallPrompt />

        {/* Update Notification */}
        <UpdateNotification onUpdate={() => window.location.reload()} />
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UserPreferencesProvider>
          <OnboardingProvider>
            <PlanProvider>
              <AccountProvider>
                <CategoryProvider>
                  <TransactionProvider>
                    <LayoutProvider>
                      <AppContent />
                    </LayoutProvider>
                  </TransactionProvider>
                </CategoryProvider>
              </AccountProvider>
            </PlanProvider>
          </OnboardingProvider>
        </UserPreferencesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
